import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import React, { useEffect, useState } from "react";
import logoIcon from "../assets/wte_logo.png";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { LaunchOutlined, LocationOn, Business, CheckCircle } from "@mui/icons-material";
import { styled, alpha } from "@mui/material/styles";
import { Box, Typography, Button } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getAllSites, getUserFromDynamoDB } from "../api/apiService";

// Styled components
const StyledPopup = styled(Box)(({ theme }) => ({
  padding: '8px',
  minWidth: '250px',
  color: theme.palette.text.primary,
}));

const PopupHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  marginBottom: '12px',
  paddingBottom: '8px',
  borderBottom: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.12)' : '1px solid rgba(0, 0, 0, 0.1)',
}));

const PopupContent = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
}));

const InfoRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
}));

const StyledButton = styled(Button)(({ theme }) => ({
  marginTop: '12px',
  color: theme.palette.primary.main,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.06),
  },
}));

const Map = () => {
  const [rows, setRows] = useState([]);
  const [allowedSiteIds, setAllowedSiteIds] = useState(new Set());
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.auth.user);
  const userRole = useSelector((state) => state.auth.user?.role);
  const canVisitAll = userRole === 'Admin' || userRole === 'Manager';
  
  useEffect(() => {
    const fetchSites = async () => {
      try {
        console.log('[Map] Fetching sites for user:', currentUser?.email, 'Role:', userRole);
        const allSites = await getAllSites();
        console.log('[Map] All sites fetched:', allSites);

        // Always show every site with coordinates on the map
        setRows(Array.isArray(allSites) ? allSites : []);

        if (canVisitAll) {
          setAllowedSiteIds(new Set((allSites || []).map((s) => s.id)));
        } else if (currentUser?.email) {
          const userData = await getUserFromDynamoDB(currentUser.email);
          const allowedSites = userData?.sites || (userData?.site?.id ? [userData.site] : []);
          setAllowedSiteIds(new Set(allowedSites.map((s) => s.id).filter(Boolean)));
        } else {
          setAllowedSiteIds(new Set());
        }
      } catch (error) {
        console.error('[Map] Error fetching sites:', error);
        console.error('[Map] Error details:', error.message, error.stack);
        setRows([]);
        setAllowedSiteIds(new Set());
      }
    };

    if (currentUser) {
      fetchSites();
    } else {
      console.log('[Map] No current user, skipping fetch');
    }
  }, [currentUser, userRole, canVisitAll]);

  const canVisitSite = (siteId) => canVisitAll || allowedSiteIds.has(siteId);

  const hasValidCoordinates = (row) => {
    const lat = parseFloat(row.xCoordinate);
    const lng = parseFloat(row.yCoordinate);
    return Number.isFinite(lat) && Number.isFinite(lng);
  };

  // Define Custom Icon
  const customIcon = L.icon({
    iconUrl: logoIcon,
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40],
  });

  return (
    <Box sx={{ 
      position: "relative", 
      height: "100%",
      borderRadius: "16px",
      overflow: "hidden",
      '& .leaflet-container': {
        height: '100%',
        width: '100%',
        borderRadius: '16px',
      },
      '& .leaflet-popup-content-wrapper': {
        borderRadius: '16px',
        boxShadow: isDark ? '0px 8px 24px rgba(0, 0, 0, 0.5)' : '0px 4px 20px rgba(0, 0, 0, 0.1)',
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
      },
      '& .leaflet-popup-content': {
        margin: '0',
      },
    }}>
      <MapContainer
        center={[20.5937, 78.9629]}
        zoom={5}
        style={{ height: "100%", width: "100%" }}
      >
        {/* Map Tile Layer */}
        {isDark ? (
          <TileLayer
            attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          />
        ) : (
          <TileLayer
            attribution='&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}

        {/* All sites with valid coords — Visit only if assigned (or Admin/Manager) */}
        {rows.filter(hasValidCoordinates).map((row) => {
          const lat = parseFloat(row.xCoordinate);
          const lng = parseFloat(row.yCoordinate);
          const visitAllowed = canVisitSite(row.id);

          return (
          <Marker
            key={row.id}
            position={[lat, lng]}
            icon={customIcon}
          >
            <Popup>
              <StyledPopup>
                <PopupHeader>
                  <Typography variant="subtitle1" sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <LocationOn sx={{ color: theme.palette.primary.main }} />
                    {canVisitAll ? row.id : (row.name || row.location || 'Site')}
                  </Typography>
                </PopupHeader>
                <PopupContent>
                  {canVisitAll && (
                    <Typography variant="h6" sx={{ 
                      color: theme.palette.text.primary,
                      fontWeight: 600,
                      fontSize: '1rem'
                    }}>
                      {row.name}
                    </Typography>
                  )}
                  <InfoRow>
                    <Business sx={{ fontSize: '1rem' }} />
                    {row.client}
                  </InfoRow>
                  <InfoRow>
                    <LocationOn sx={{ fontSize: '1rem' }} />
                    {row.location}
                  </InfoRow>
                  <InfoRow>
                    <LocationOn sx={{ fontSize: '1rem' }} />
                    Coords: {lat.toFixed(5)}, {lng.toFixed(5)}
                  </InfoRow>
                  <InfoRow>
                    <CheckCircle sx={{ 
                      fontSize: '1rem',
                      color: row.status === 'Active' ? theme.palette.status?.normalGreen || '#4CAF50' : theme.palette.warning.main
                    }} />
                    Status: {row.status}
                  </InfoRow>
                  {visitAllowed ? (
                    <StyledButton
                      startIcon={<LaunchOutlined />}
                      onClick={() =>
                        navigate("/individual-project", {
                          state: { projectId: row.id },
                        })
                      }
                    >
                      Visit Project
                    </StyledButton>
                  ) : (
                    <Typography
                      variant="caption"
                      sx={{ mt: 1.5, color: theme.palette.text.secondary, display: 'block' }}
                    >
                     
                    </Typography>
                  )}
                </PopupContent>
              </StyledPopup>
            </Popup>
          </Marker>
          );
        })}
      </MapContainer>
    </Box>
  );
};

export default Map;
